/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  Button,
  BUTTON_FORMAT_STRONG,
  BUTTON_THEME_PRIMARY,
  ButtonList,
  ButtonListProps,
  ButtonProps,
  Row,
} from '@squiz/sds';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFileStore } from '../../../../Services/file-store';
import { FILE_EXPLORER_STRINGS } from './FileExplorer.constants';
import { useFileExplorerStore } from './FileExplorer.state';
import { CreateFolderDialogLauncher } from './elements/CreateFolderDialogLauncher/CreateFolderDialogLauncher';
import { FileExplorerActions } from './elements/FileExplorerActions/FileExplorerActions';
import { FileExplorerPath } from './elements/FileExplorerPath/FileExplorerPath';
import { FileExplorerTable } from './elements/FileExplorerTable/FileExplorerTable';
import { InfectionAlert } from './elements/InfectionAlert/InfectionAlert';

export const FileExplorer = (): JSX.Element => {
  const { t } = useTranslation();
  const { loadingState } = useFileStore();
  const { setStagedFilesToUpload } = useFileExplorerStore();
  const [showFileId, setShowFileId] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileListChange = (event: React.ChangeEvent): void => {
    const target = event.target as HTMLInputElement;
    if (target.files) {
      setStagedFilesToUpload(target.files);
    }
  };
  const labels = {
    cancel: t(FILE_EXPLORER_STRINGS.cancel),
    create: t(FILE_EXPLORER_STRINGS.create),
    createFolder: t(FILE_EXPLORER_STRINGS.createFolder),
    folderName: t(FILE_EXPLORER_STRINGS.folderName),
    uploadFiles: t(FILE_EXPLORER_STRINGS.uploadFiles),
  };
  const uploadButtonRef = useRef<HTMLButtonElement>(null);
  const uploadButtonProps: ButtonProps = {
    classes: 'file-explorer__upload-btn',
    format: BUTTON_FORMAT_STRONG,
    isLoading: loadingState.fileUploadLoading,
    onClick: () => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    },
    text: labels.uploadFiles,
    theme: BUTTON_THEME_PRIMARY,
  };

  const buttonListProps: ButtonListProps = {
    buttonKeys: ['createFolder', 'uploadButton'],
    buttons: [
      <CreateFolderDialogLauncher key={'createFolder'} />,
      <Button {...uploadButtonProps} key={'uploadButton'} ref={uploadButtonRef} />,
    ],
    classes: 'file-explorer__button-list',
  };

  return (
    <div className="file-explorer">
      <input
        className="file-explorer__upload-btn-file-input"
        id="file-explorer__upload-btn-file-input"
        multiple
        onChange={handleFileListChange}
        ref={fileInputRef}
        type="file"
      />
      <ButtonList {...buttonListProps} />
      <InfectionAlert />

      <Row>
        <div className="file-explorer__controls">
          <FileExplorerPath />
          <FileExplorerActions onFileIdToggle={setShowFileId} />
        </div>
        <div className="file-explorer__content">
          <FileExplorerTable showFileId={showFileId} />
        </div>
      </Row>
    </div>
  );
};
